import { useEffect } from 'react';

interface PerformanceOptimizationOptions {
  enablePreload?: boolean;
  enablePrefetch?: boolean;
  enableResourceHints?: boolean;
  enableImageOptimization?: boolean;
}

export const usePerformanceOptimization = (options: PerformanceOptimizationOptions = {}) => {
  const {
    enablePreload = true,
    enablePrefetch = true,
    enableResourceHints: _enableResourceHints = true,
    enableImageOptimization = true,
  } = options;
  void _enableResourceHints; // hints are emitted only via SEOMetaTags (Helmet); option kept for API compatibility

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Preload critical resources
    if (enablePreload) {
      const preloadResources: Array<{
        href: string;
        as: 'font' | 'image' | 'script' | 'style';
        type?: string;
        crossorigin?: 'anonymous' | 'use-credentials';
      }> = [
        // Add critical fonts, images, or scripts here
        // Example: { href: '/fonts/critical-font.woff2', as: 'font', type: 'font/woff2', crossorigin: 'anonymous' }
      ];

      preloadResources.forEach(resource => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource.href;
        link.as = resource.as;
        if (resource.type) link.type = resource.type;
        if (resource.crossorigin) link.crossOrigin = resource.crossorigin;
        document.head.appendChild(link);
      });
    }

    // Prefetch next likely resources
    if (enablePrefetch) {
      const prefetchResources: string[] = [
        // Add resources that are likely to be needed next
        // Example: '/api/services', '/api/testimonials'
      ];

      prefetchResources.forEach(href => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = href;
        document.head.appendChild(link);
      });
    }

    // Resource hints (dns-prefetch / preconnect): handled only in SEOMetaTags (Helmet), not here — avoids duplicate head links

    // Optimize images
    if (enableImageOptimization) {
      // Add intersection observer for all images
      const images = document.querySelectorAll('img[data-src]');
      
      if (images.length > 0) {
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              img.src = img.dataset.src || '';
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          });
        }, {
          rootMargin: '50px 0px',
          threshold: 0.01
        });

        images.forEach(img => imageObserver.observe(img));
      }
    }

    // Optimize critical rendering path
    const optimizeCriticalPath = () => {
      // Remove unused CSS
      const unusedCSS = document.querySelectorAll('link[rel="stylesheet"]:not([data-critical])');
      unusedCSS.forEach(link => {
        if (!link.hasAttribute('data-critical')) {
          // Mark as non-critical for later loading
          const linkElement = link as HTMLLinkElement;
          linkElement.media = 'print';
          linkElement.onload = () => {
            linkElement.media = 'all';
          };
        }
      });

      // Defer non-critical JavaScript
      const scripts = document.querySelectorAll('script[src]:not([data-critical])');
      scripts.forEach(script => {
        if (!script.hasAttribute('data-critical')) {
          const scriptElement = script as HTMLScriptElement;
          scriptElement.defer = true;
        }
      });
    };

    // Run optimization after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', optimizeCriticalPath);
    } else {
      optimizeCriticalPath();
    }

    // Performance monitoring
    const measurePerformance = () => {
      if ('performance' in window) {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        if (navigation) {
          /* console.group('🚀 Performance Metrics (Detailed)');
          console.log('DNS Lookup:', navigation.domainLookupEnd - navigation.domainLookupStart, 'ms');
          console.log('TCP Connection:', navigation.connectEnd - navigation.connectStart, 'ms');
          console.log('TLS Negotiation:', navigation.secureConnectionStart > 0 ? navigation.connectEnd - navigation.secureConnectionStart : 0, 'ms');
          console.log('Request/Response:', navigation.responseEnd - navigation.requestStart, 'ms');
          console.log('DOM Processing:', navigation.domContentLoadedEventEnd - navigation.responseEnd, 'ms');
          console.log('Page Load:', navigation.loadEventEnd - navigation.fetchStart, 'ms');
          console.groupEnd(); */
        }
      }
    };

    // Measure performance after page load
    window.addEventListener('load', measurePerformance);

    // Cleanup
    return () => {
      window.removeEventListener('load', measurePerformance);
    };
  }, [enablePreload, enablePrefetch, enableImageOptimization]);

  return {
    // Return optimization utilities if needed
    optimizeImage: (src: string, width?: number, quality: number = 75) => {
      if (src.includes('supabase.co')) {
        const url = new URL(src);
        url.searchParams.set('quality', quality.toString());
        if (width) url.searchParams.set('width', width.toString());
        url.searchParams.set('format', 'webp');
        return url.toString();
      }
      return src;
    },
  };
};
